import type { ColumnFiltersState, SortingState } from "@tanstack/react-table";

/**
 * One list's viewport, in the row-identity coordinates DataTable emits.
 * Structurally the `DataTableViewportState` it is written from — kept as its
 * own declaration so `src/ui` never has to import from `src/lib`.
 */
export interface ListViewState {
  /** `getRowId` of the row that was at the top of the scrollport. */
  anchorRowId: string | null;
  /** That row's index in the sorted/filtered row model when captured. */
  anchorIndex: number;
  /** Pixels the scrollport had passed the anchor row's top edge (>= 0). */
  anchorOffset: number;
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
}

/**
 * Session-scoped and in-memory ONLY — never a browser storage API (the ban in
 * `scripts/check_static_invariants.py` is enforced verbatim, and a hard reload
 * starting fresh is the semantics this feature wants anyway).
 *
 * LRU-bounded: a workspace has a bounded number of live list scopes, and an
 * unbounded map would pin every filter permutation a session ever visited.
 */
const MAX_LIST_VIEW_STATES = 32;
const listViewStates = new Map<string, ListViewState>();

/** Reads one scope's stored viewport, marking it most-recently-used. */
export function getListViewState(key: string): ListViewState | undefined {
  const state = listViewStates.get(key);
  if (state === undefined) {
    return undefined;
  }
  listViewStates.delete(key);
  listViewStates.set(key, state);
  return state;
}

/** Stores one scope's viewport, evicting the least-recently-used entry. */
export function setListViewState(key: string, state: ListViewState): void {
  listViewStates.delete(key);
  listViewStates.set(key, state);
  if (listViewStates.size > MAX_LIST_VIEW_STATES) {
    const oldestKey = listViewStates.keys().next().value;
    if (oldestKey !== undefined) {
      listViewStates.delete(oldestKey);
    }
  }
}

/** Drops one scope's stored viewport (a list reset to page one starts at
 *  the top, never at the anchor its previous pages held). */
export function deleteListViewState(key: string): void {
  listViewStates.delete(key);
}

/** Test-only reset; nothing in `src/` calls this. */
export function clearListViewStates(): void {
  listViewStates.clear();
}
